/**
 * NodeConfigPanel.jsx — Canvas V2 right-side property editor (#181).
 *
 * Slides in when a non-SubWorkflow node is clicked on /canvas-v2. Hosts
 * type-specific fieldsets for the 6 most-used task types plus a generic
 * JSON fallback for the long tail — so any node's config is reachable
 * in two clicks from the canvas, and changes flow back through
 * lastPayloadRef with dirty tracking + save plumbing intact.
 *
 * Architecture
 * ────────────
 * - Controlled by a single `editingNode` prop from CanvasPageV2 (the
 *   full canvas node: `{id, type, label, config}`). Panel keeps its
 *   own local draft so form state doesn't thrash the canvas on every
 *   keystroke.
 * - On "Apply" (or ⌘S while the panel has focus): calls `onChange` with
 *   the merged node; parent replaces the entry in lastPayloadRef, re-
 *   lays out, and flips the dirty flag.
 * - Type-specific fieldsets are keyed by canvas type string. Unknown
 *   types get the generic JSON fieldset only — power users can still
 *   edit everything, even if there's no bespoke form yet.
 *
 * The panel is deliberately a presentational component with no
 * network or layout calls — CanvasPageV2 owns all side effects.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { V2_PAL, V2_STONE, V2_BRAND } from './nodes/v2Palette';

// ── Task type → form renderer map ───────────────────────────────────
// Each entry returns an array of Fieldset descriptors the main render
// walks. Keep these pure — no side effects, no network calls.
// Each descriptor: { key, label, render: (draft, setDraft) => ReactNode }

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const TERM_STATUSES = ['COMPLETED', 'FAILED'];
const FORK_TYPES = ['FORK_JOIN', 'FORK_JOIN_DYNAMIC'];
const EVAL_TYPES = ['value-param', 'javascript'];

function humanTypeLabel(t) {
  if (!t) return 'Node';
  // snake_case → Title Case
  return String(t).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function setConfigField(draft, setDraft, key, value) {
  setDraft({
    ...draft,
    config: { ...(draft.config || {}), [key]: value },
  });
}

// Reusable row widgets. Keeping them inline (not standalone exports)
// so the panel is a single file — easier to scan, easier to style.
function LabeledInput({ label, value, onChange, placeholder, mono, type = 'text' }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: V2_STONE[600], marginBottom: 3, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</div>
      <input
        type={type}
        value={value == null ? '' : value}
        onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        style={{
          width: '100%', padding: '6px 8px', fontSize: 12,
          fontFamily: mono ? 'SFMono-Regular, Menlo, monospace' : undefined,
          color: V2_STONE[900], background: '#ffffff',
          border: `1px solid ${V2_STONE[300]}`, borderRadius: 5,
          outline: 'none', boxSizing: 'border-box',
        }}
      />
    </label>
  );
}

function LabeledSelect({ label, value, options, onChange }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: V2_STONE[600], marginBottom: 3, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</div>
      <select
        value={value == null ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '6px 8px', fontSize: 12,
          color: V2_STONE[900], background: '#ffffff',
          border: `1px solid ${V2_STONE[300]}`, borderRadius: 5,
          outline: 'none', boxSizing: 'border-box',
        }}
      >
        {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </label>
  );
}

function LabeledTextarea({ label, value, onChange, placeholder, rows = 6 }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: V2_STONE[600], marginBottom: 3, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</div>
      <textarea
        value={value == null ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        spellCheck={false}
        style={{
          width: '100%', padding: '8px 10px', fontSize: 11,
          fontFamily: 'SFMono-Regular, Menlo, monospace',
          color: V2_STONE[900], background: '#ffffff',
          border: `1px solid ${V2_STONE[300]}`, borderRadius: 5,
          outline: 'none', resize: 'vertical', boxSizing: 'border-box',
        }}
      />
    </label>
  );
}

function LabeledCheckbox({ label, checked, onChange, hint }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, color: V2_STONE[700] }}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: V2_BRAND.primary }}
      />
      <span>
        {label}
        {hint && <span style={{ color: V2_STONE[500], marginLeft: 6 }}>· {hint}</span>}
      </span>
    </label>
  );
}

// ── Task-type fieldsets ─────────────────────────────────────────────

function HttpTaskFields({ draft, setDraft }) {
  const cfg = draft.config || {};
  return (
    <>
      <LabeledSelect label="Method" value={cfg.method || 'GET'} options={HTTP_METHODS}
        onChange={(v) => setConfigField(draft, setDraft, 'method', v)} />
      <LabeledInput label="URL" value={cfg.url} mono
        placeholder="https://api.example.com/endpoint"
        onChange={(v) => setConfigField(draft, setDraft, 'url', v)} />
      <LabeledTextarea label="Headers (JSON)" value={
        typeof cfg.headers === 'object' && cfg.headers != null ? JSON.stringify(cfg.headers, null, 2) : (cfg.headers || '')
      } rows={3} placeholder={'{\n  "Authorization": "Bearer …"\n}'}
        onChange={(v) => {
          // Accept JSON or a raw string; store parsed when valid, raw otherwise.
          try {
            const parsed = v.trim() === '' ? {} : JSON.parse(v);
            setConfigField(draft, setDraft, 'headers', parsed);
          } catch (_) {
            setConfigField(draft, setDraft, 'headers', v);
          }
        }} />
      <LabeledTextarea label="Body" value={
        typeof cfg.body === 'object' && cfg.body != null ? JSON.stringify(cfg.body, null, 2) : (cfg.body || '')
      } rows={5} placeholder='{"claim_id": "${workflow.input.id}"}'
        onChange={(v) => {
          try {
            const parsed = v.trim() === '' ? null : JSON.parse(v);
            setConfigField(draft, setDraft, 'body', parsed);
          } catch (_) {
            setConfigField(draft, setDraft, 'body', v);
          }
        }} />
      <LabeledInput type="number" label="Retry count" value={cfg.retryCount}
        placeholder="0"
        onChange={(v) => setConfigField(draft, setDraft, 'retryCount', v)} />
    </>
  );
}

function ScriptTaskFields({ draft, setDraft }) {
  const cfg = draft.config || {};
  return (
    <>
      <LabeledSelect label="Source type"
        value={cfg.sourceType || 'INLINE'}
        options={['INLINE', 'JSON_JQ_TRANSFORM']}
        onChange={(v) => setConfigField(draft, setDraft, 'sourceType', v)} />
      <LabeledTextarea label="Script / expression" value={cfg.script || cfg.expression || ''} rows={10}
        placeholder="// ${workflow.input}\n// write js that returns the task's output"
        onChange={(v) => setConfigField(draft, setDraft, cfg.sourceType === 'JSON_JQ_TRANSFORM' ? 'expression' : 'script', v)} />
    </>
  );
}

function ConditionFields({ draft, setDraft }) {
  const cfg = draft.config || {};
  return (
    <>
      <LabeledInput label="Expression" value={cfg.expression} mono
        placeholder="$.validationResult"
        onChange={(v) => setConfigField(draft, setDraft, 'expression', v)} />
      <LabeledSelect label="Evaluator" value={cfg.evaluatorType || 'value-param'} options={EVAL_TYPES}
        onChange={(v) => setConfigField(draft, setDraft, 'evaluatorType', v)} />
      <div style={{
        fontSize: 11, color: V2_STONE[500], marginTop: 4, padding: '8px 10px',
        background: V2_STONE[100], borderRadius: 5, lineHeight: 1.5,
      }}>
        Cases are wired via outgoing connections.
        Add a case by dragging from this switch&apos;s output port
        on the canvas — the connection&apos;s <code>sourceHandle</code>
        becomes the case name.
      </div>
    </>
  );
}

function ForkFields({ draft, setDraft }) {
  const cfg = draft.config || {};
  const isDynamic = cfg.sourceType === 'FORK_JOIN_DYNAMIC';
  return (
    <>
      <LabeledSelect label="Fork type" value={cfg.sourceType || 'FORK_JOIN'} options={FORK_TYPES}
        onChange={(v) => setConfigField(draft, setDraft, 'sourceType', v)} />
      {isDynamic && (
        <LabeledInput label="List expression" value={cfg.listExpression} mono
          placeholder="${workflow.input.items}"
          onChange={(v) => setConfigField(draft, setDraft, 'listExpression', v)} />
      )}
      {!isDynamic && (
        <div style={{
          fontSize: 11, color: V2_STONE[500], marginTop: 4, padding: '8px 10px',
          background: V2_STONE[100], borderRadius: 5, lineHeight: 1.5,
        }}>
          Branches are wired on the canvas. Drag from the fork&apos;s
          output port to create each parallel branch; the join downstream
          decides wait-mode (ALL / ANY).
        </div>
      )}
    </>
  );
}

function SubWorkflowFields({ draft, setDraft }) {
  const cfg = draft.config || {};
  return (
    <>
      <LabeledInput label="Workflow ref name" value={cfg.subWorkflowName || cfg.name}
        placeholder="health_claim_evaluation"
        onChange={(v) => setConfigField(draft, setDraft, 'subWorkflowName', v)} />
      <LabeledInput type="number" label="Version" value={cfg.subWorkflowVersion}
        placeholder="1"
        onChange={(v) => setConfigField(draft, setDraft, 'subWorkflowVersion', v)} />
      <LabeledCheckbox label="Async (start-and-forget)"
        hint={'use with a downstream Wait task for webhooks'}
        checked={!!cfg.async || cfg.invocationMode === 'async'}
        onChange={(v) => setConfigField(draft, setDraft, 'async', v)} />
    </>
  );
}

function TerminateFields({ draft, setDraft }) {
  const cfg = draft.config || {};
  return (
    <>
      <LabeledSelect label="Termination status"
        value={cfg.terminationStatus || cfg.status || 'COMPLETED'}
        options={TERM_STATUSES}
        onChange={(v) => setConfigField(draft, setDraft, 'terminationStatus', v)} />
      <LabeledInput label="Reason" value={cfg.reason}
        placeholder="Invalid claim — rejected by validator"
        onChange={(v) => setConfigField(draft, setDraft, 'reason', v)} />
    </>
  );
}

// ── Type dispatcher ─────────────────────────────────────────────────

const FIELD_RENDERERS = {
  http_task: HttpTaskFields,
  script_task: ScriptTaskFields,
  condition: ConditionFields,
  fork: ForkFields,
  sub_workflow: SubWorkflowFields,
  async_sub_workflow: SubWorkflowFields,
  terminate: TerminateFields,
};

// ── Main component ──────────────────────────────────────────────────

export default function NodeConfigPanel({ editingNode, onChange, onClose, readOnly }) {
  // Local draft — reset whenever the target node changes. Apply writes
  // back to the parent; dismissing without Apply throws away the draft.
  const [draft, setDraft] = useState(editingNode);
  const [showJsonFallback, setShowJsonFallback] = useState(false);
  const [jsonDraft, setJsonDraft] = useState(null);
  const [jsonError, setJsonError] = useState(null);

  useEffect(() => {
    setDraft(editingNode);
    setShowJsonFallback(false);
    setJsonDraft(null);
    setJsonError(null);
  }, [editingNode && editingNode.id]); // eslint-disable-line

  const specificRenderer = draft ? FIELD_RENDERERS[draft.type] : null;

  const apply = useCallback(() => {
    if (!draft) return;
    onChange(draft);
  }, [draft, onChange]);

  // ⌘Enter → apply, Esc → close. The input elements inside don't block
  // these since we listen on the panel's own key capture.
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!readOnly) apply();
    }
  }, [apply, onClose, readOnly]);

  const applyJsonDraft = useCallback(() => {
    if (!draft) return;
    try {
      const parsed = jsonDraft == null ? (draft.config || {}) : JSON.parse(jsonDraft);
      setJsonError(null);
      onChange({ ...draft, config: parsed });
    } catch (err) {
      setJsonError(err.message || String(err));
    }
  }, [draft, jsonDraft, onChange]);

  const panelColor = useMemo(() => {
    if (!draft) return V2_STONE[500];
    const t = (draft.type || '').toLowerCase();
    if (t === 'condition') return V2_PAL.switch.head.border;
    if (t === 'fork') return V2_PAL.fork.head.border;
    if (t === 'loop') return V2_PAL.loop.head.border;
    if (t === 'sub_workflow') return V2_PAL.subWorkflow.sync.border;
    if (t === 'async_sub_workflow') return V2_PAL.subWorkflow.async.border;
    if (t === 'terminate') return V2_PAL.terminate.failed.border;
    return V2_STONE[500];
  }, [draft]);

  if (!editingNode || !draft) return null;

  return (
    <div
      onKeyDown={handleKeyDown}
      style={{
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: 380, maxWidth: '90%',
        zIndex: 15,
        background: '#ffffff',
        borderLeft: `1px solid ${V2_STONE[300]}`,
        boxShadow: '-6px 0 20px rgba(15, 23, 42, 0.08)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{
        padding: '12px 14px',
        borderBottom: `1px solid ${V2_STONE[200]}`,
        display: 'flex', alignItems: 'center', gap: 10,
        background: `linear-gradient(135deg, ${V2_BRAND.soft} 0%, #ffffff 70%)`,
      }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%', background: panelColor,
          flexShrink: 0,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
            color: V2_STONE[600], textTransform: 'uppercase',
          }}>{humanTypeLabel(draft.type)}</div>
          <div style={{
            fontSize: 13, fontWeight: 600, color: V2_STONE[900],
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{draft.label || draft.id}</div>
        </div>
        <button
          onClick={onClose}
          title="Close (Esc)"
          style={{
            padding: '4px 8px', fontSize: 12, color: V2_STONE[600],
            background: 'transparent', border: `1px solid ${V2_STONE[300]}`,
            borderRadius: 4, cursor: 'pointer',
          }}
        >✕</button>
      </div>

      {readOnly && (
        <div style={{
          padding: '6px 14px', fontSize: 11, color: V2_STONE[600],
          background: V2_STONE[100], borderBottom: `1px solid ${V2_STONE[200]}`,
        }}>Read-only — this canvas is in execution view.</div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
        <LabeledInput label="Label" value={draft.label}
          onChange={(v) => setDraft({ ...draft, label: v })} />
        <LabeledInput label="ID (read-only)" value={draft.id}
          onChange={() => {}} mono />

        {specificRenderer
          ? specificRenderer({ draft, setDraft })
          : (
            <div style={{
              fontSize: 11, color: V2_STONE[600], padding: '10px 12px',
              background: V2_STONE[100], borderRadius: 5, marginBottom: 10,
              lineHeight: 1.5,
            }}>
              No bespoke editor for type <code>{draft.type}</code> yet —
              use the JSON fallback below to edit raw config.
            </div>
          )
        }

        <div style={{
          marginTop: 14, paddingTop: 12,
          borderTop: `1px dashed ${V2_STONE[200]}`,
        }}>
          <button
            onClick={() => {
              setShowJsonFallback((v) => {
                const next = !v;
                if (next) setJsonDraft(JSON.stringify(draft.config || {}, null, 2));
                return next;
              });
            }}
            style={{
              fontSize: 11, fontWeight: 600, color: V2_STONE[700],
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 0,
            }}
          >{showJsonFallback ? '▼ Raw config (JSON)' : '▶ Raw config (JSON)'}</button>
          {showJsonFallback && (
            <div style={{ marginTop: 8 }}>
              <LabeledTextarea label="config" value={jsonDraft}
                rows={10} placeholder="{}"
                onChange={(v) => { setJsonDraft(v); setJsonError(null); }} />
              {jsonError && (
                <div style={{
                  fontSize: 10, color: '#9f1239', marginTop: -6, marginBottom: 10,
                  padding: 6, background: '#fef2f2', borderRadius: 4,
                }}>JSON parse error: {jsonError}</div>
              )}
              <button
                onClick={applyJsonDraft}
                disabled={readOnly}
                style={{
                  padding: '5px 10px', fontSize: 11, fontWeight: 600,
                  color: readOnly ? V2_STONE[500] : '#ffffff',
                  background: readOnly ? V2_STONE[200] : V2_STONE[700],
                  border: 'none', borderRadius: 4,
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >Apply JSON</button>
            </div>
          )}
        </div>
      </div>

      <div style={{
        padding: '10px 14px',
        borderTop: `1px solid ${V2_STONE[200]}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8,
        background: V2_STONE[50] || '#fafafa',
      }}>
        <span style={{ fontSize: 10, color: V2_STONE[500] }}>
          <kbd style={kbdStyle}>⌘↵</kbd> apply · <kbd style={kbdStyle}>esc</kbd> close
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 10px', fontSize: 11, fontWeight: 500,
              color: V2_STONE[700],
              background: '#ffffff',
              border: `1px solid ${V2_STONE[300]}`, borderRadius: 5,
              cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            onClick={apply}
            disabled={readOnly}
            style={{
              padding: '6px 12px', fontSize: 11, fontWeight: 600,
              color: '#ffffff',
              background: readOnly ? V2_STONE[400] : V2_BRAND.primary,
              border: 'none', borderRadius: 5,
              cursor: readOnly ? 'default' : 'pointer',
            }}
          >Apply</button>
        </div>
      </div>
    </div>
  );
}

const kbdStyle = {
  display: 'inline-block',
  margin: '0 2px',
  padding: '1px 5px',
  fontFamily: 'SFMono-Regular, Menlo, monospace',
  fontSize: 10,
  color: V2_STONE[700],
  background: '#ffffff',
  border: `1px solid ${V2_STONE[300]}`,
  borderRadius: 3,
};
